// LSP-17342 (review 5024093113, CRITICAL): setOrganizationId used to `return` on paths where the
// switch had NOT happened — no client, or an org id that could not be read — which the caller could
// not tell apart from a successful switch. The router's authenticated-boot reconcile then continued
// with an access token still scoped to the PREVIOUS organization: the same wrong-organization defect
// this PR exists to fix, arriving through the RESOLVE path instead of the reject path. Every
// failed-to-switch exit now throws this instead, so "the promise resolved" can only ever mean the
// switch happened, was already current, or was deliberately abandoned for sign-out.
//
// Classified as retryable: both causes (Auth0 client creation failing, an unreadable org claim) are
// overwhelmingly transient auth-client problems, so the router defers the route and replays rather
// than burning a terminal dialog on something that self-heals.
export default class OrganizationSwitchNotPerformedError extends Error {
	// readonly for the same reason as Auth0RedirectSuppressedError: the router branches on
	// organizationSwitchNotPerformed to decide whether to defer or continue boot.
	readonly organizationSwitchNotPerformed: boolean;
	readonly reason: string;
	readonly organizationId: string | null;

	constructor(reason: string, organizationId?: string | null) {
		super(`Organization switch to ${organizationId ?? "unknown"} did not happen: ${reason}`);
		this.name = "OrganizationSwitchNotPerformedError";
		this.organizationSwitchNotPerformed = true;
		this.reason = reason;
		this.organizationId = organizationId ?? null;
	}
}

// Duck-typed on the marker property alone, for the same reasons as
// isAuth0RedirectSuppressedError: the error crosses an `await` and a module boundary, `instanceof`
// breaks under a duplicated module instance, and a name- or message-based check would misclassify
// unrelated failures into a branch that decides whether boot may continue.
export function isOrganizationSwitchNotPerformedError(error: unknown): error is OrganizationSwitchNotPerformedError {
	return (error as { organizationSwitchNotPerformed?: unknown } | null | undefined)?.organizationSwitchNotPerformed === true;
}
